import { Box, Button, Card, Divider, Typography } from '@material-ui/core'
import React from 'react'
import { Translate } from 'react-localize-redux'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  checkIsAfterDateTime,
  diffDate,
  displayDateForEventPage,
} from '../../../utils/formatDate'
import useStyles from './styles'

const SubscriptionsList = ({
  isLoading,
  subscriptions,
  onCancel,
  onRenew,
  onManagePlan,
}) => {
  const classes = useStyles()

  const getColorForStatus = (status) => {
    return ['active', 'trialing'].includes(status) ? 'primary' : 'error'
  }

  const getColorForTrialDays = (trialEndsAt) => {
    const daysLeft = diffDate(trialEndsAt, new Date()) + 1

    return daysLeft > 7 ? 'primary' : 'error'
  }

  return (
    <>
      <Box mt={3} sm={{ ml: { xs: 0, sm: 0, lg: 3 } }}>
        <Typography className={classes.billingheading}>
          <Translate id="plans.billing-and-subscription" />
        </Typography>
      </Box>
      {subscriptions.map((subscription) => (
        <Card
          key={subscription.uuid}
          className={classes.currentPlanCard}
          variant="outlined"
        >
          <Box className={classes.section}>
            <Box className={classes.width25}>
              <Typography className={classes.Label}>
                <Translate id="plans.current-plan" />
              </Typography>
            </Box>
            <Box className={clsx(classes.planinfo, classes.width35)}>
              <Box display="flex" alignItems="center">
                <Typography className={classes.subLabel}>
                  {subscription.plan_display_name}
                </Typography>
              </Box>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography className={classes.subtext} color={getColorForStatus(subscription.status)}>
                  <Translate id={`plans.status_${subscription.status}`} />
                </Typography>

                {['manual', 'stripe', 'paypal'].includes(subscription.gateway) && subscription.trial_ends_at && (
                  <Typography className={classes.subtext} color={getColorForTrialDays(subscription.trial_ends_at)}>
                    <Translate id="plans.trial-ends-on" />&nbsp;
                    {displayDateForEventPage(subscription.trial_ends_at)}
                  </Typography>
                )}

                {['manual', 'stripe', 'paypal'].includes(subscription.gateway) && subscription.ends_at && (
                  <Typography className={classes.subtext} color="error">
                    {checkIsAfterDateTime(new Date(), subscription.ends_at)
                      ? <Translate id="plans.expired-on" />
                      : <Translate id="plans.ends-on" />
                    }
                    &nbsp;
                    {displayDateForEventPage(subscription.ends_at)}
                  </Typography>
                )}
              </div>
            </Box>
            <Box display="flex" alignItems="center" justifyContent='end' className={classes.width40}>
              {subscription.can.includes('manage') && subscription.updatable && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => onManagePlan(subscription)}
                  className={classes.button}
                >
                  <Translate id="plans.manage-plan" />
                </Button>
              )}
              {subscription.can.includes('manage') && subscription.renewable && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={isLoading}
                  onClick={() => onRenew(subscription)}
                >
                  <Translate id="plans.renew-plan" />
                </Button>
              )}
              {subscription.can.includes('manage') && subscription.cancellable && (
                <Button
                  variant="text"
                  color="primary"
                  className={classes.canceltext}
                  disabled={isLoading}
                  onClick={() => onCancel(subscription)}
                >
                  <Translate id="team.cancel-subscription" />
                </Button>
              )}
            </Box>
          </Box>
          <Divider />
          <Box className={classes.section1}>
            <Box className={classes.width25}>
              <Typography className={classes.Label}>
                <Translate id="checkout.payment-methods" />
              </Typography>
            </Box>
            <Box className={classes.width75}>
              <Typography className={classes.subLabel}>
                {subscription.gateway.replace(/^./, (char) =>
                  char.toUpperCase()
                )}
              </Typography>
            </Box>
            <Box />
          </Box>
        </Card>
      ))}
    </>
  )
}

SubscriptionsList.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      plan_display_name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      trial_ends_at: PropTypes.string,
      ends_at: PropTypes.string,
      gateway: PropTypes.string.isRequired,
      cancellable: PropTypes.bool.isRequired,
      renewable: PropTypes.bool.isRequired,
      updatable: PropTypes.bool.isRequired,
      can: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  onCancel: PropTypes.func.isRequired,
  onRenew: PropTypes.func.isRequired,
  onManagePlan: PropTypes.func.isRequired,
}

export default SubscriptionsList
